import { ConnectButton } from '@rainbow-me/rainbowkit';
import { AppSidebar } from "@/components/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { EcosystemCard } from "@/components/ecosystem-card";
import { ResourceLink } from "@/components/resource-link";
import { ExternalResourceLink } from "@/components/external-resource-link";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/tabs";
import { Link } from "react-router-dom";
import {
  RiGlobalLine,
  RiFileTextLine,
  RiGithubFill,
  RiBookOpenLine,
  RiTwitterXFill,
  RiTelegramFill,
  RiDiscordFill,
  RiYoutubeFill,
  RiBarChartLine,
} from "@remixicon/react";
import {larissaChain} from "@/configs/chain";

// Core ecosystem tools
const coreTools = [
  {
    id: "block-explorer",
    title: "Block Explorer",
    description: "Explore transactions, blocks, and accounts on the Larissa blockchain",
    icon: <RiGlobalLine className="size-6" />,
    link: larissaChain.blockExplorers.default.url,
  },
  {
    id: "network-stats",
    title: "Network Stats",
    description: "View real-time statistics about the Larissa network",
    icon: <RiBarChartLine className="size-6" />,
    link: "https://stats.larissa.network",
  },
];

// Official links
const officialLinks = [
  {
    title: "Official Larissa Website",
    icon: <RiGlobalLine />,
    link: "https://larissa.network/",
  },
  {
    title: "Official SILO Website",
    icon: <RiGlobalLine />,
    link: "https://www.silos3.com/",
  },
  {
    title: "Larissa Whitepaper",
    icon: <RiFileTextLine />,
    link: "https://larissa.network/whitepaper.pdf",
  },
  {
    title: "SILO Whitepaper",
    icon: <RiFileTextLine />,
    link: "https://www.silos3.com/SILOWhitePaper.pdf",
  },
  {
    title: "Github",
    icon: <RiGithubFill />,
    link: "https://github.com/LarissaBlockchain/Core",
  },
  {
    title: "Docs",
    icon: <RiBookOpenLine />,
    link: "https://docs.larissa.network/",
  },
  {
    title: "Twitter",
    icon: <RiTwitterXFill />,
    link: "https://twitter.com/larissanetwork",
  },
  {
    title: "Telegram Chat",
    icon: <RiTelegramFill />,
    link: "https://t.me/larissaconnect",
  },
  {
    title: "Discord",
    icon: <RiDiscordFill />,
    link: "https://discord.gg/52fevT6F5r",
  },
  {
    title: "YouTube",
    icon: <RiYoutubeFill />,
    link: "https://www.youtube.com/@LARISSANETWORK",
  },
];

// Other links
const otherLinks = [
  {
    title: "CoinGecko",
    logoSrc: "/images/logos/coingecko.png",
    link: "https://www.coingecko.com/en/coins/larissa-blockchain",
  },
  {
    title: "CoinMarketCap",
    logoSrc: "/images/logos/coinmarketcap.webp",
    link: "#",
    disabled: true,
    note: "TBD",
  },
  {
    title: "Coinpaprika",
    logoSrc: "/images/logos/coinpaprika.png",
    link: "https://coinpaprika.com/coin/lrs-larissa/",
  },
  {
    title: "Livecoinwatch",
    logoSrc: "/images/logos/livewatchcoin.png",
    link: "https://www.livecoinwatch.com/price/LarissaNetwork-LRS",
  },
  {
    title: "Hashrate.no",
    logoSrc: "/images/logos/hashrate_no.png",
    link: "https://hashrate.no/coins/LRS",
  },
  {
    title: "MiningPoolStats",
    logoSrc: "/images/logos/miningpoolstats.png",
    link: "https://miningpoolstats.stream/larissa",
  },
  {
    title: "Fxempire",
    logoSrc: "/images/logos/fxempire.webp",
    link: "https://www.fxempire.com/crypto/larissa-blockchain/profile",
  },
];

export default function BlockchainEcosystemPage() {
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/blockchain">Blockchain</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>Ecosystem</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <ConnectButton />
            </header>

            <div className="py-6">
              <div className="flex items-center mb-6">
                <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                <h1 className="text-3xl font-bold">Larissa Ecosystem</h1>
              </div>

              <p className="text-muted-foreground mb-8 max-w-3xl">
                Explore the growing ecosystem of tools, resources, and communities that make up the Larissa network.
                From official resources to community tools, everything you need to get started with Larissa is here.
              </p>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              <section className="mb-12">
                <div className="flex items-center mb-6">
                  <div className="w-1 h-6 bg-[#FF8A3C] mr-3"></div>
                  <h2 className="text-2xl font-bold">Core Tools</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {coreTools.map(tool => (
                    <EcosystemCard
                      key={tool.id}
                      title={tool.title}
                      description={tool.description}
                      icon={tool.icon}
                      link={tool.link}
                      sublinks={[]}
                    />
                  ))}
                </div>
              </section>

              <section>
                <Tabs defaultValue="official" className="mb-8">
                  <TabsList className="mb-6">
                    <TabsTrigger value="official">Official Links</TabsTrigger>
                    <TabsTrigger value="other">Other Resources</TabsTrigger>
                  </TabsList>

                  <TabsContent value="official">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {officialLinks.map((link, index) => (
                        <ResourceLink
                          key={index}
                          title={link.title}
                          icon={link.icon}
                          link={link.link}
                        />
                      ))}
                    </div>
                  </TabsContent>

                  <TabsContent value="other">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {otherLinks.map((link, index) => (
                        <ExternalResourceLink
                          key={index}
                          title={link.title}
                          logoSrc={link.logoSrc}
                          link={link.link}
                          disabled={link.disabled}
                          note={link.note}
                        />
                      ))}
                    </div>
                  </TabsContent>
                </Tabs>
              </section>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}





